import BaseActionAbstract from 'cio/app-models/action-abstracts/base';
import { computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { DAYS, WEEKDAYS, WEEKENDS, daysToSet, setToDays, dayItemValue } from 'cio/utils/delivery-window';
import { CUSTOMER_TIME_ZONE } from 'cio/utils/time-zones';
import { task } from 'ember-concurrency';

let DAYS_DIRTY_DEPENDENCY = `days.{${DAYS.join(',')}}`;
const EDITABLE_PROPERTIES = ['preconditions', 'startTime', 'endTime', 'zone', 'fallbackZone'];
const DIRTY_DEPENDENCIES = [
  'preconditions',
  'startTime',
  'endTime',
  'zone',
  'fallbackZone',
  DAYS_DIRTY_DEPENDENCY,
  'action.{startTime,endTime,zone,fallbackZone,days}',
];

export default BaseActionAbstract.extend({
  type: 'time_window',

  action: null,
  actionType: null,

  _editableProperties: EDITABLE_PROPERTIES,
  _dirtyDependencies: DIRTY_DEPENDENCIES,

  preconditions: null,
  startTime: null,
  endTime: null,
  zone: null,
  fallbackZone: null,
  days: null,

  anyTime: computed('startTime', 'endTime', {
    get() {
      let { startTime, endTime } = this;

      return startTime === '0:00' && endTime === '23:59';
    },

    set(key, value) {
      if (value) {
        this.setProperties({ startTime: '0:00', endTime: '23:59' });
      }

      return value;
    },
  }),

  anyDay: equal('selectedDayValue', 'any'),

  selectedDayValue: computed('days', {
    get() {
      return dayItemValue(this.days);
    },

    set(key, value) {
      let { days } = this;

      if (value === 'weekdays') {
        WEEKDAYS.forEach((day) => days.set(day, true));
        WEEKENDS.forEach((day) => days.set(day, false));
      } else if (value === 'weekends') {
        WEEKENDS.forEach((day) => days.set(day, true));
        WEEKDAYS.forEach((day) => days.set(day, false));
      } else {
        DAYS.forEach((day) => days.set(day, true));
      }

      return value;
    },
  }),

  prepareForEdit() {
    this._super(...arguments);

    this.set('days', daysToSet(this.action.days));
  },

  prepareForSave() {
    let action = this._super(...arguments);

    action.set('days', setToDays(this.days));

    return action;
  },

  _isDirty: computed('action.days', DAYS_DIRTY_DEPENDENCY, function () {
    let actionDays = daysToSet(this.action.days);
    let { days } = this;

    return Object.keys(days).any((d) => days[d] !== actionDays[d]);
  }),

  illegalFallbackZoneSelected: computed('zone', 'fallbackZone', function () {
    if (this.zone === CUSTOMER_TIME_ZONE.value) {
      return isEmpty(this.fallbackZone);
    } else {
      return false;
    }
  }),

  errors: computed('anyDay', 'anyTime', 'illegalFallbackZoneSelected', function () {
    // The values in the conditions are actually booleans
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (this.anyDay && this.anyTime) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (this.illegalFallbackZoneSelected) {
      return {
        fallbackZone: ['Please select a fallback time zone'],
      };
    }

    return;
  }),

  // eslint-disable-next-line require-yield
  saveTask: task(function* () {
    let action = this.prepareForSave();

    try {
      let response = yield action.save();
      this.prepareForEdit();
      return response;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  }).drop(),

  // eslint-disable-next-line require-yield
  deleteTask: task(function* () {
    return this.action.campaign.removeActionFromWorkflow(this.action);
    // return yield this.action.deleteViaAPI();
  }).drop(),
});
